const u = (id, w = 1200) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;

const IMG = {
  straight: u("1610276173132-c47d148ab626"),
  parallel: u("1778272617843-bdda47b64bad"),
  lShaped: u("1663811396777-05505d999151"),
  uShaped: u("1729108281270-d3824c261d51"),
  jShaped: u("1657524497227-66242be7ee6e"),
  island: u("1759147960461-b74a7e9a75d4"),
  peninsula: u("1684928365167-e91916573122"),
  galley: u("1772476361206-958e402f98d3"),
  open: u("1502005097973-6a7082348e28"),
  small: u("1628745277862-bc0b2d68c50c"),
  compact: u("1721902022158-8b0ada9c604b"),
  largeFamily: u("1717429541792-5c59021d6ceb"),
  minimal: u("1536853273994-5d28a729533d"),
  contemporary: u("1586208958839-06c17cacdf08"),
  luxury: u("1682888813913-e13f18692019"),
  wood: u("1512916194211-3f2b7f5f7de3"),
  handleless: u("1622372738946-62e02505feb3"),
  matte: u("1643949915134-73a4c880f7c7"),
  gloss: u("1722605090433-41d1183a792d"),
  functional: u("1559554704-0f74b35a8718"),
};

export const KITCHENS = [
  {
    slug: "straight-kitchen",
    name: "The Straight Kitchen",
    category: "Straight Kitchen",
    description:
      "A single clean run of base and wall units — the honest workhorse for narrow urban homes, engineered to feel effortless.",
    image: IMG.straight,
    gallery: [IMG.straight, IMG.minimal, IMG.matte],
    layout: "Single-wall run along one wall",
    recommendedUse: "Studio apartments, narrow kitchens and utility-first homes",
    storage: ["Full-height tower units", "Deep base drawers", "Overhead lofts where height allows"],
    finishes: ["Gloss laminate", "Matte laminate", "Wood-textured laminate"],
    hardware: ["Soft-close hinges", "Tandem drawers", "Cutlery organisers"],
  },
  {
    slug: "parallel-kitchen",
    name: "The Parallel Kitchen",
    category: "Parallel Kitchen",
    description:
      "Two facing runs that turn a compact corridor into a hard-working galley of storage and counter space.",
    image: IMG.parallel,
    gallery: [IMG.parallel, IMG.galley, IMG.wood],
    layout: "Two parallel platform runs",
    recommendedUse: "Closed kitchens in apartments; efficient two-cook workflow",
    storage: ["Tall pantry pull-out", "Corner carousels", "Full-depth base cabinets"],
    finishes: ["Matte laminate", "High-gloss laminate", "Dark wood textures"],
    hardware: ["Magic corner units", "Soft-close drawers", "Bottle pull-outs"],
  },
  {
    slug: "l-shaped-kitchen",
    name: "The L-Shaped Kitchen",
    category: "L-Shaped Kitchen",
    description:
      "India's favourite layout — two adjoining runs that open up floor space while hiding serious storage along the corner.",
    image: IMG.lShaped,
    gallery: [IMG.lShaped, IMG.functional, IMG.luxury],
    layout: "Two adjoining walls in an L",
    recommendedUse: "Open and semi-open homes; family kitchens of most sizes",
    storage: ["Corner solutions", "Base carousels", "Wall units and lofts"],
    finishes: ["Matte", "Wood finish", "Contemporary solids"],
    hardware: ["Soft-close hinges", "Pull-out baskets", "Under-sink organisers"],
  },
  {
    slug: "u-shaped-kitchen",
    name: "The U-Shaped Kitchen",
    category: "U-Shaped Kitchen",
    description:
      "Three walls of continuous counter and storage — maximum capacity for families that cook big, every day.",
    image: IMG.uShaped,
    gallery: [IMG.uShaped, IMG.largeFamily, IMG.gloss],
    layout: "Three-sided counter run",
    recommendedUse: "Larger homes and joint-family cooking",
    storage: ["Tower units", "Corner pull-outs", "Dedicated appliance bays"],
    finishes: ["Gloss", "Matte", "Wood finish"],
    hardware: ["Tall-unit mechanisms", "Tandem drawers", "Waste-management systems"],
  },
  {
    slug: "j-shaped-kitchen",
    name: "The J-Shaped Kitchen",
    category: "J-Shaped Kitchen",
    description:
      "A U with a shorter fourth leg — extra counter and a natural breakfast nook, built on the same engineered carcass.",
    image: IMG.jShaped,
    gallery: [IMG.jShaped, IMG.uShaped, IMG.open],
    layout: "U-layout with a short fourth run",
    recommendedUse: "Mid-to-large kitchens that want a casual dining edge",
    storage: ["Breakfast counter storage", "Base drawers", "Wall cabinets"],
    finishes: ["Matte", "Wood finish", "Two-tone combinations"],
    hardware: ["Soft-close systems", "Cutlery organisers", "Pull-out baskets"],
  },
  {
    slug: "island-kitchen",
    name: "The Island Kitchen",
    category: "Island Kitchen",
    description:
      "A freestanding island at the heart of the plan — prep space, seating and storage in one sculptural centre piece.",
    image: IMG.island,
    gallery: [IMG.island, IMG.luxury, IMG.largeFamily],
    layout: "Working wall plus freestanding island",
    recommendedUse: "Open-plan homes with generous floor area",
    storage: ["Island base cabinets", "Double-side access", "Backup pantry walls"],
    finishes: ["Matte", "Gloss", "Wood with stone tops"],
    hardware: ["Soft-close drawers", "Bar-unit fittings", "Bin pull-outs"],
  },
  {
    slug: "peninsula-kitchen",
    name: "The Peninsula Kitchen",
    category: "Peninsula Kitchen",
    description:
      "Island convenience with one end anchored to the wall — counter, seating and partition in a single connected build.",
    image: IMG.peninsula,
    gallery: [IMG.peninsula, IMG.island, IMG.contemporary],
    layout: "L or parallel run with attached counter extension",
    recommendedUse: "Open kitchens that need definition between rooms",
    storage: ["Peninsula base storage", "Bar drawers", "Display niches"],
    finishes: ["Wood finish", "Matte", "Two-tone"],
    hardware: ["Soft-close fittings", "Cutlery systems", "Wine and bottle racks"],
  },
  {
    slug: "galley-kitchen",
    name: "The Galley Kitchen",
    category: "Galley Kitchen",
    description:
      "Compact, linear and relentlessly efficient — everything within two steps, engineered for daily speed.",
    image: IMG.galley,
    gallery: [IMG.galley, IMG.parallel, IMG.small],
    layout: "Single or double corridor run",
    recommendedUse: "Apartments and service kitchens",
    storage: ["Full-height columns", "Slot organisers", "Overhead units"],
    finishes: ["Matte", "Gloss", "Compact wood tones"],
    hardware: ["Tandem drawers", "Soft-close hinges", "Slot pantries"],
  },
  {
    slug: "open-kitchen",
    name: "The Open Kitchen",
    category: "Open Kitchen",
    description:
      "Built to be seen from the living room — clean sightlines, disciplined storage and a finish that holds its own.",
    image: IMG.open,
    gallery: [IMG.open, IMG.island, IMG.minimal],
    layout: "Open plan, often with island or peninsula",
    recommendedUse: "Modern open-plan apartments and villas",
    storage: ["Concealed pantries", "Handleless base storage", "Display shelves"],
    finishes: ["Handleless matte", "Gloss", "Wood accents"],
    hardware: ["Handleless fittings", "Soft-close systems", "Integrated bins"],
  },
  {
    slug: "small-kitchen",
    name: "The Small Kitchen",
    category: "Small Kitchen",
    description:
      "Every inch has a purpose — slim drawers, tall pull-outs and clever corners that make a compact plan feel generous.",
    image: IMG.small,
    gallery: [IMG.small, IMG.compact, IMG.galley],
    layout: "Straight or compact L",
    recommendedUse: "Compact apartments and starter homes",
    storage: ["Bottle pull-outs", "Slim span drawers", "Loft storage"],
    finishes: ["Light gloss", "Matte pastels", "Wood tones"],
    hardware: ["Pull-out baskets", "Corner units", "Under-sink solutions"],
  },
  {
    slug: "compact-kitchen",
    name: "The Compact Kitchen",
    category: "Compact Kitchen",
    description:
      "A tight footprint, fully engineered — proper carcass, proper hardware, zero wasted millimetres.",
    image: IMG.compact,
    gallery: [IMG.compact, IMG.small, IMG.functional],
    layout: "Single-wall or mini L",
    recommendedUse: "Rental homes, cottages and secondary kitchens",
    storage: ["Multi-function drawers", "Wall shelves", "Appliance niches"],
    finishes: ["Matte", "Gloss", "Wood finish"],
    hardware: ["Soft-close hinges", "Compact organisers", "Slot fittings"],
  },
  {
    slug: "large-family-kitchen",
    name: "The Large Family Kitchen",
    category: "Large Family Kitchen",
    description:
      "Generous runs, twin-zone workflow and storage for the way big Indian families actually cook.",
    image: IMG.largeFamily,
    gallery: [IMG.largeFamily, IMG.uShaped, IMG.luxury],
    layout: "U-shape, L+island or parallel-plus",
    recommendedUse: "Large households and frequent entertaining",
    storage: ["Twin pantry towers", "Wide tandem drawers", "Dedicated masala and thali zones"],
    finishes: ["Matte", "Wood", "Luxury stone pairings"],
    hardware: ["Heavy-duty tandem drawers", "Tall-unit systems", "Magic corners"],
  },
  {
    slug: "modern-minimal-kitchen",
    name: "The Modern Minimal Kitchen",
    category: "Modern Minimal Kitchen",
    description:
      "Flat fronts, quiet tones and zero visual noise — minimalism carried on a fully engineered carcass.",
    image: IMG.minimal,
    gallery: [IMG.minimal, IMG.handleless, IMG.matte],
    layout: "Any layout, executed minimal",
    recommendedUse: "Design-led homes that want calm, clean lines",
    storage: ["Concealed everything", "Inner drawers", "Integrated bins"],
    finishes: ["Handleless matte", "Super-matte", "Stone-look laminates"],
    hardware: ["Handleless systems", "Soft-close fittings", "Push-open options"],
  },
  {
    slug: "contemporary-kitchen",
    name: "The Contemporary Kitchen",
    category: "Contemporary Kitchen",
    description:
      "Current design language — colour, texture and contrast balanced against the warmth of natural wood.",
    image: IMG.contemporary,
    gallery: [IMG.contemporary, IMG.peninsula, IMG.luxury],
    layout: "L, island or open plans",
    recommendedUse: "Modern homes that want character without clutter",
    storage: ["Open + closed mix", "Base organisers", "Display niches"],
    finishes: ["Colour laminates", "Wood + solid pairings", "Two-tone"],
    hardware: ["Soft-close systems", "Pull-out baskets", "Cutlery organisers"],
  },
  {
    slug: "luxury-kitchen",
    name: "The Luxury Kitchen",
    category: "Luxury Kitchen",
    description:
      "Marble-scale statements on an engineered core — premium finishes, premium motion, premium everything.",
    image: IMG.luxury,
    gallery: [IMG.luxury, IMG.island, IMG.largeFamily],
    layout: "Island and large-format plans",
    recommendedUse: "Villas, farmhouses and flagship homes",
    storage: ["Tall pantry walls", "Island storage", "Concealed appliance bays"],
    finishes: ["Premium gloss", "Deep mattes", "Natural wood veneers"],
    hardware: ["Premium branded fittings", "Tall-unit systems", "Sensor lighting options"],
  },
  {
    slug: "wood-finish-kitchen",
    name: "The Wood Finish Kitchen",
    category: "Wood Finish Kitchen",
    description:
      "The warmth of timber tones on laminate-bonded plywood — natural character, engineered durability.",
    image: IMG.wood,
    gallery: [IMG.wood, IMG.handleless, IMG.peninsula],
    layout: "Any layout, wood-forward finishes",
    recommendedUse: "Homes that want warmth with modern function",
    storage: ["Base drawers", "Wall units", "Open display shelves"],
    finishes: ["Oak tones", "Walnut tones", "Textured wood laminates"],
    hardware: ["Matte-black fittings", "Soft-close systems", "Cutlery organisers"],
  },
  {
    slug: "handleless-kitchen",
    name: "The Handleless Kitchen",
    category: "Handleless Kitchen",
    description:
      "Clean fronts with integrated grip channels — seamless to look at, smooth to operate, easy to live with.",
    image: IMG.handleless,
    gallery: [IMG.handleless, IMG.minimal, IMG.gloss],
    layout: "Straight, L or island",
    recommendedUse: "Minimal, modern interiors",
    storage: ["Handleless base units", "Inner drawers", "Concealed pantries"],
    finishes: ["Super-matte", "Gloss", "Wood tones"],
    hardware: ["Handleless profiles", "Soft-close fittings", "Push-to-open options"],
  },
  {
    slug: "matte-finish-kitchen",
    name: "The Matte Finish Kitchen",
    category: "Matte Finish Kitchen",
    description:
      "Soft-touch matte fronts that resist fingerprints and glare — understated, elegant, hard-working.",
    image: IMG.matte,
    gallery: [IMG.matte, IMG.minimal, IMG.wood],
    layout: "Any layout in matte finishes",
    recommendedUse: "Homes wanting a premium, low-glare surface",
    storage: ["Full extension drawers", "Tall units", "Under-sink organisers"],
    finishes: ["Matte laminates", "Deep charcoal matte", "Earthy tones"],
    hardware: ["Soft-close hinges", "Tandem drawers", "Discreet handles"],
  },
  {
    slug: "gloss-finish-kitchen",
    name: "The Gloss Finish Kitchen",
    category: "Gloss Finish Kitchen",
    description:
      "High-sheen fronts that multiply light — a bright, polished look sealed onto a bonded, pressed core.",
    image: IMG.gloss,
    gallery: [IMG.gloss, IMG.luxury, IMG.straight],
    layout: "Straight, parallel or island",
    recommendedUse: "Homes that want brightness and drama",
    storage: ["Base drawers", "Wall units", "Corner pull-outs"],
    finishes: ["High-gloss laminates", "Light Ivories", "Bold colour gloss"],
    hardware: ["Soft-close systems", "Pull-out baskets", "Chrome-finish fittings"],
  },
  {
    slug: "indian-functional-kitchen",
    name: "The Indian Functional Kitchen",
    category: "Indian Functional Kitchen",
    description:
      "Designed around real Indian cooking — masala drawers, tawas, urulis and room for the pressure cookers.",
    image: IMG.functional,
    gallery: [IMG.functional, IMG.lShaped, IMG.parallel],
    layout: "L, U or parallel — planned for the Indian workflow",
    recommendedUse: "Every home where heavy cooking happens daily",
    storage: ["Masala & thali drawers", "Tawa and uruli slots", "Container organisers"],
    finishes: ["Matte", "Wood finish", "Practical gloss"],
    hardware: ["Heavy-duty drawers", "Bottle pull-outs", "Waste-management systems"],
  },
];

export const KITCHEN_CATEGORIES = [
  "Straight Kitchen",
  "Parallel Kitchen",
  "L-Shaped Kitchen",
  "U-Shaped Kitchen",
  "J-Shaped Kitchen",
  "Island Kitchen",
  "Peninsula Kitchen",
  "Galley Kitchen",
  "Open Kitchen",
  "Small Kitchen",
  "Compact Kitchen",
  "Large Family Kitchen",
  "Modern Minimal Kitchen",
  "Contemporary Kitchen",
  "Luxury Kitchen",
  "Wood Finish Kitchen",
  "Handleless Kitchen",
  "Matte Finish Kitchen",
  "Gloss Finish Kitchen",
  "Indian Functional Kitchen",
];
